import { routerPaths } from "./routerPaths";

/**
 * Permission-gated landing pages, in order; the resolver returns the first one
 * the role can access.
 *
 * Home leads the list — every subgroup holds `homepage_page_access`
 * (permissions/mapping.py), so it always matches once the Homepage v2 board is
 * enabled for the company. Until then it is skipped entirely and the legacy
 * file lists below it are what a user lands on, which is what lets the board
 * ship to production switched off.
 */
const GATED_LANDING_CANDIDATES: ReadonlyArray<{ path: string; permission: string }> = [
    { path: routerPaths.home, permission: "homepage_page_access" },
    { path: routerPaths.files.list, permission: "files_all_page_access" },
    { path: routerPaths.files.tbgl, permission: "tbgl_files_page_access" },
    { path: routerPaths.files.finJournals, permission: "finjournal_files_view" },
];

/**
 * All Files is the fallback: every role holds `files_all_page_access`, so it is
 * reachable whatever the gated candidates say.
 */
const FALLBACK_LANDING_PATH = routerPaths.files.list;

export type TResolveLandingPathOptions = {
    /** The company's `homepage_v2_enabled` flag. Defaults to false so a caller
     * that has not loaded the flags yet can never land someone on a board
     * their company cannot open. */
    homepageEnabled?: boolean;
};

/**
 * Resolve the first page the given permission set can access. Used to redirect a
 * (possibly emulated) role away from a page it cannot view, instead of showing a
 * "no access" message. Always returns a path — All Files is the guaranteed fallback.
 */
export const resolveLandingPath = (
    permissions: string[],
    { homepageEnabled = false }: TResolveLandingPathOptions = {},
): string => {
    const candidates = homepageEnabled
        ? GATED_LANDING_CANDIDATES
        : GATED_LANDING_CANDIDATES.filter((candidate) => candidate.path !== routerPaths.home);
    const match = candidates.find((candidate) => permissions.includes(candidate.permission));
    return match?.path ?? FALLBACK_LANDING_PATH;
};
